import { useEffect } from 'react'
import { useSearchParams } from 'react-router'
import { api, call } from '@/api/client'
import type { ApiFailure, Schema } from '@/api/client'
import { useQuery } from '@/api/query'

/**
 * Which site a per-site Settings panel is about.
 *
 * Settings consolidation (2026-09-15). `/settings` is per organisation and the
 * run schedule, publish target and approval gating are per site. The choice
 * lives in `?site=` so it carries from one per-site panel to the next and
 * survives a reload; the chip is a control only when there is something to
 * choose between. A single-site tenant never sees a picker.
 */
export type SiteRow = Schema<'SiteListResponse'>['sites'][number]

export interface SettingsSite {
  status: 'loading' | 'error' | 'ready'
  failure: ApiFailure | null
  retry: () => void
  sites: SiteRow[]
  /** `null` while loading, on error, and when the tenant has no site. */
  site: SiteRow | null
  select: (id: string) => void
}

const PARAM = 'site'

export function useSettingsSite(): SettingsSite {
  const [params, setParams] = useSearchParams()
  const query = useQuery((signal) => call(() => api.GET('/api/v1/seo/sites', { signal })), [])
  const sites = [...(query.state.data?.sites ?? [])].sort((a, b) =>
    a.domain.localeCompare(b.domain),
  )
  const requested = params.get(PARAM)
  const site = sites.find((row) => row.id === requested) ?? sites[0] ?? null

  // Rewrite an unknown or missing `?site=` once the list is known, so the URL
  // says what the panel shows and a copied link lands on the same site.
  useEffect(() => {
    if (site !== null && requested !== site.id) {
      const next = new URLSearchParams(params)
      next.set(PARAM, site.id)
      setParams(next, { replace: true })
    }
  }, [site, requested, params, setParams])

  return {
    status: query.state.status,
    failure: query.state.status === 'error' ? query.state.failure : null,
    retry: query.refetch,
    sites,
    site,
    select: (id) => {
      const next = new URLSearchParams(params)
      next.set(PARAM, id)
      setParams(next)
    },
  }
}
